/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LegalRequirementsV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Modal, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import { useDispatch } from "react-redux";
import { HandleApiActions, legalRequirements, adminSettingsAPI } from "../../../redux/actions";
import CustomLegalStandard from "../../../components/customComponentsV2/legalreq/CustomLegalStandard";
import CustomLegalRequirements from "../../../components/customComponentsV2/legalreq/CustomLegalRequirements";
import AddDialog from "../../../components/customComponentsV2/projectConfig/AddDialog";
import CustomFormTextField from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";
import CustomDatePicker from "../../../components/customComponentsV2/common/datepicker/CustomDatePicker";
import moment from "moment";
import CustomTextFieldWithChips from "../../../components/customComponentsV2/common/textField/CustomTextFieldWithChips";
import {
  ChipsTextField,
  CustomExportButton,
  CustomImportButton,
} from "../../../components";
import dayjs from "dayjs";
import LegalCustomDatePicker from "../../../components/customComponentsV2/common/datepicker/LegalCustomDatePicker";
import customParseFormat from "dayjs/plugin/customParseFormat";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CloseIcon from "@mui/icons-material/Close";
import CustomStatusOverrideSelect from "../../../components/customComponentsV2/legalreq/CustomStatusOverrideSelect";
import customConsole from "../../../config/customConsole";
import { showToastMessage } from "../../../redux/actions/actionHandler";
import CustomFilterTemplate from "../../../components/customComponentsV2/common/filter/CustomFilterTemplate";
import FilterTemplate from "../../../components/customComponentsV2/common/Templates/FilterTemplate";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import { TICKETS_TYPES } from "../../../config/appConstants";

function LegalStandardTypeCheckboxGroup({
  value = [],
  onChange,
  label,
  required = false,
  data = [],
  disabled,
}) {
  const selectedValues = Array.isArray(value) ? value : [];

  const handleTypeChange = (typeValue) => (event) => {
    const isSelectedType = (selectedValue) =>
      String(selectedValue) === String(typeValue);
    const nextValues = event.target.checked
      ? [...selectedValues, typeValue]
      : selectedValues.filter((selectedValue) => !isSelectedType(selectedValue));

    onChange(nextValues);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {label && (
        <Typography
          sx={{
            mb: 0.8,
            color: "#6f7174",
            fontSize: "0.9rem",
            fontWeight: 500,
            display: "block",
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {data.map((item) => (
          <CustomCheckBoxV2
            key={item.value}
            label={item.label}
            value={selectedValues.some(
              (selectedValue) => String(selectedValue) === String(item.value),
            )}
            onChange={handleTypeChange(item.value)}
            disabled={disabled}
          />
        ))}
      </Box>
    </Box>
  );
}

const normalizeModuleTypes = (moduleType) => {
  const moduleTypes = Array.isArray(moduleType) ? moduleType : [moduleType];

  return moduleTypes
    .filter((type) => type !== undefined && type !== null && type !== "")
    .map(Number)
    .filter((type) => !Number.isNaN(type));
};

function LegalRequirementsV2() {
  dayjs.extend(customParseFormat);
  const dispatch = useDispatch();
  const [adminDefaultData, setAdminDefaultData] = useState({});

  // ✅ States
  const [standardOptionsList, setStandardOptionsList] = useState([]);
  const [allLegalStandardsData, setAllLegalStandardsData] = useState([]);
  const [legalStandardsData, setLegalStandardsData] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [openAddLS, setOpenAddLS] = useState(false);

  const [openUpdateRevision, setOpenUpdateRevision] = useState();
  const [openEditLS, setOpenEditLS] = useState(false);
  const [openForVersion, setOpenForVersion] = useState(false);
  const [standardData, setStandardData] = useState({
    name: "",
    standard_number: "",
    revision: "",
    description: "",
    summary: "",
    chapters: [],
    module_type: [3],
    current_time: moment().format(),
    markets: [],
    effective_from: null,
    valid_till: null,
  });
  const [initialStandardData, setInitialStandardData] = useState({});
  const [lsId, setLSId] = useState("");
  const [isOverrideChecked, setIsOverrideChecked] = useState(false);
  const [status, setStatus] = useState("");
  const [exportSelectedLsDetails, setExportSelectedLsDetails] = useState(null);
  const [exportSelectedLsLrDetails, setExportSelectedLsLrDetails] =
    useState(null);

  const [
    selectedFilterByStatusObjBeforeClick,
    setSelectedFilterByStatusObjBeforeClick,
  ] = useState([]);
  const [initialStatus, setInitialStatus] = useState("");
  const addLSNull = () => {
    setStandardData({
      name: "",
      standard_number: "",
      revision: "",
      description: "",
      summary: "",
      chapters: [],
      module_type: [3],
      current_time: moment().format(),
      markets: [],
      effective_from: null,
      valid_till: null,
    });
  };

  const handleOpenCloseAddLS = (value) => {
    setOpenAddLS(value);

    if (value) {
      addLSNull();
    }
  };

  const handleOpenCloseEditLS = (value) => {
    setOpenEditLS(value);
  };

  console.log("----selectedStandard----", selectedStandard);

  const fetchInitialData = async () => {
    try {
      const adminSettings = await dispatch(
        HandleApiActions({ ...adminSettingsAPI.get_admin_settings }),
      );

      setAdminDefaultData(adminSettings || []);
    } catch (error) {
      console.error("❌ Failed to load initial data:", error);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [openAddLS]);

  console.log(
    "standardDatatataa",
    dayjs(standardData?.effective_from?._d).format("MM-YYYY"),
  );

  const handleEditClick = (value, standard) => {
    setLSId(standard?.ls_id);
    console.log("Hello");
    console.log("standardstandard", standard);
    console.log(standard?.effective_from);
    console.log(dayjs(standard?.effective_from, "MM-YYYY", true).toDate());
    const formatMonthYear = (date) =>
      date ? dayjs(date).startOf("month") : null;
    setOpenEditLS(value);
    setStandardData({
      name: standard?.name || "",
      standard_number:
        standard?.standard_number || standard?.standardNumber || "",
      revision: standard?.revision || "",
      description: standard?.description || "",
      summary: standard?.summary || "",
      chapters: standard?.chapters || [],
      module_type: normalizeModuleTypes(standard?.module_type),
      markets: standard?.markets || [],
      effective_from: /^\d{2}-\d{4}$/.test(standard?.effective_from)
        ? dayjs(standard.effective_from, "MM-YYYY").startOf("month")
        : formatMonthYear(standard?.effective_from),

      valid_till: /^\d{2}-\d{4}$/.test(standard?.valid_till)
        ? dayjs(standard.valid_till, "MM-YYYY").startOf("month")
        : formatMonthYear(standard?.valid_till),
      legal_req: standard?.legal_req || [],
    });

    setStatus(standard?.type_of_changes || "");
    setInitialStatus(standard?.type_of_changes || "");

    setInitialStandardData({
      name: standard?.name || "",
      standard_number:
        standard?.standard_number || standard?.standardNumber || "",
      revision: standard?.revision || "",
      description: standard?.description || "",
      summary: standard?.summary || "",
      chapters: standard?.chapters || [],
      module_type: normalizeModuleTypes(standard?.module_type),
      markets: standard?.markets || [],
      effective_from: /^\d{2}-\d{4}$/.test(standard?.effective_from)
        ? dayjs(standard.effective_from, "MM-YYYY").startOf("month")
        : formatMonthYear(standard?.effective_from),

      valid_till: /^\d{2}-\d{4}$/.test(standard?.valid_till)
        ? dayjs(standard.valid_till, "MM-YYYY").startOf("month")
        : formatMonthYear(standard?.valid_till),
      legal_req: standard?.legal_req || [],
    });
    console.log("StandardDataStandardData", standardData);
  };

  // ✅ Fetch Legal Standards List
  const handleGetLegalStandardList = async () => {
    try {
      const params = {
        page_number: 1,
        page_size: 2500,
        sort_by: "ASC",
        sort_column: "created_at",
        ...(selectedFilterByStatusObjBeforeClick?.length > 0 && {
          module_type: selectedFilterByStatusObjBeforeClick
            .map((data) => data.value)
            .join(","),
        }),
      };

      const resp = await dispatch(
        HandleApiActions({
          ...legalRequirements.get_legal_standard_main_details,
          params,
        }),
      );

      console.log("---- Legal Standard Details ----", resp);
      return resp;
    } catch (error) {
      console.error("Error fetching Legal Standard list:", error);
      throw error;
    }
  };

  // ✅ Group and sort logic
  const groupAndSortLegalStandards = (data) => {
    if (!Array.isArray(data)) return [];

    const grouped = data.reduce((acc, item) => {
      const key = item.standard_number;
      if (!acc[key]) acc[key] = [];
      acc[key].push({
        standard_number: item.standard_number,
        version: item.version,
        revision: item.revision,
        ls_id: item.ls_id,
        ...item,
      });
      return acc;
    }, {});

    const sortedGroupedDetails = Object.entries(grouped).map(
      ([standard_number, items]) => ({
        standard_number,
        items: items.sort((a, b) => a.version - b.version),
      }),
    );

    return sortedGroupedDetails;
  };

  const handleAddLegalStandard = async (reqObj) => {
    console.log("Add Legal Standard:", reqObj);

    try {
      const resp = await dispatch(
        HandleApiActions({
          ...legalRequirements.add_legal_standard,
          params: reqObj,
          show_toast: true,
        }),
      );

      if (!resp?.error) {
        await refreshLegalStandards();

        handleOpenCloseAddLS(false);
      }
    } catch (error) {
      console.error("Error adding legal standard:", error);
    }
  };
  const reqObj = {
    ...standardData,
    module_type: Array.isArray(standardData.module_type)
      ? standardData.module_type
      : [],

    effective_from: standardData.effective_from
      ? dayjs(standardData.effective_from).format("MM-YYYY")
      : null,

    valid_till: standardData.valid_till
      ? dayjs(standardData.valid_till).format("MM-YYYY")
      : null,
  };

  console.log("reqObjreqObjreqObj", reqObj);

  const singleColumnRows = [2, 3, 4, 5, 6, 7, 8];
  const componentMap = {
    ChipsTextField,
    CustomFormTextField,
    CustomTextFieldWithChips,
    CustomMultiSelectV2,
    CustomDatePicker,
    LegalCustomDatePicker,
    CustomStatusOverrideSelect,
    CustomCheckBoxV2,
    LegalStandardTypeCheckboxGroup,
  };
  // const item = legalStandardsData?.[0]?.items || [];
  const isAddDisabledLS = () => {
    const d = standardData;
    return !(
      d.summary.trim() &&
      d.standard_number?.trim() &&
      Array.isArray(d.module_type) &&
      d.module_type.length > 0 &&
      d.name?.trim() &&
      d.description?.trim() &&
      d.revision?.trim() &&
      d.effective_from &&
      d.valid_till &&
      Array.isArray(d.chapters) &&
      d.chapters.length > 0 &&
      Array.isArray(d.markets) &&
      d.markets.length > 0
    );
  };

  const formLayoutAdd = [
    {
      component: "CustomFormTextField",
      props: {
        label: "Legal Standard No",
        required: true,
        type: "text",
        fldType: "text",
        value: standardData.standard_number,
        setValue: (val) =>
          setStandardData((prev) => ({ ...prev, standard_number: val })),
        placeholder: "Enter Legal Standard No",
      },
    },
    {
      component: "LegalStandardTypeCheckboxGroup",
      props: {
        label: "Type",
        onlyLR: true,
        required: true,
        data: [
          { value: TICKETS_TYPES.VEHICLE_SYSTEM, label: "Vehicle System" },
          { value: TICKETS_TYPES.VEHICLE_COMPONENT, label: "Vehicle Component" },
          { value: TICKETS_TYPES.EE, label: "EE Component" },
          { value: TICKETS_TYPES.SWM, label: "Software Module" },
        ],
        value: standardData.module_type,
        onChange: (selectedTypes) =>
          setStandardData((prev) => ({
            ...prev,
            module_type: selectedTypes,
          })),
      },
    },
    {
      component: "CustomFormTextField",
      props: {
        label: "Legal Standard Name",
        required: true,
        value: standardData.name,
        setValue: (val) => setStandardData((prev) => ({ ...prev, name: val })),
        placeholder: "Enter Legal Standard Name",
      },
    },
    {
      component: "CustomFormTextField",
      props: {
        label: "Description",
        required: true,
        value: standardData.description,
        setValue: (val) =>
          setStandardData((prev) => ({ ...prev, description: val })),
        placeholder: "Enter Description",
      },
    },
    {
      component: "CustomFormTextField",
      props: {
        label: "Summary",
        required: true,
        value: standardData.summary,
        setValue: (val) =>
          setStandardData((prev) => ({ ...prev, summary: val })),
        placeholder: "Enter Summary",
      },
    },
    {
      component: "CustomFormTextField",
      props: {
        label: "Revision",
        required: true,
        value: standardData.revision,
        setValue: (val) =>
          setStandardData((prev) => ({ ...prev, revision: val })),
        placeholder: "Enter Revision No",
      },
    },
    {
      component: "LegalCustomDatePicker",
      props: {
        label: "Effective From - Valid Till",

        effectiveFrom: standardData.effective_from,
        validTill: standardData.valid_till,
        setEffectiveFrom: (val) =>
          setStandardData((prev) => ({
            ...prev,
            effective_from: val,
          })),

        setValidTill: (val) =>
          setStandardData((prev) => ({
            ...prev,
            valid_till: val,
          })),

        required: true,
      },
    },
    {
      component: "ChipsTextField",
      props: {
        size: "small",
        label: "Chapters",

        variant: "outlined",
        chips: Array.isArray(standardData?.chapters)
          ? standardData.chapters
          : [],
        setChips: (newChips) => {
          setStandardData((prev) => ({
            ...prev,
            chapters: newChips,
          }));
        },

        required: true,
      },
    },
    // {
    //   component: "CustomTextFieldWithChips",
    //   props: {
    //     label: "Add Chapters",
    //     placeholder: "Type a Chapter...",
    //     value: chapterInput,
    //     setValue: setChapterInput,
    //     onChangeChips: handleChaptersChange,
    //     required: true,
    //   },
    // },
    {
      component: "CustomMultiSelectV2",
      props: {
        placeholder: "Select Delivery Market",
        label: "Delivery Market",
        required: true,

        // >>> Correct selected values (must be array of {label, value} objects)
        selectedValues: Array.isArray(standardData.markets)
          ? standardData.markets.map((m) => ({ label: m, value: m }))
          : [],

        // >>> Correct setter (store only values inside standardData)
        setSelectedValues: (vals) =>
          setStandardData((prev) => ({
            ...prev,
            markets: vals.map((item) => item.value),
          })),

        // >>> Correct options list
        optionsList:
          adminDefaultData?.data?.product_delivery_markets?.map((item) => ({
            label: item,
            value: item,
          })) || [],
      },
    },
  ];

  const formLayoutEdit = [
    {
      component: "CustomFormTextField",
      props: {
        label: "Legal Standard No",
        disabled: true,
        required: true,
        type: "text",
        fldType: "text",
        value: standardData.standard_number,
        setValue: (val) =>
          setStandardData((prev) => ({ ...prev, standard_number: val })),
        placeholder: "Enter Legal Standard No",
      },
    },
    {
      component: "LegalStandardTypeCheckboxGroup",
      props: {
        label: "Type",
        required: true,
        onlyLR: true,
        // disabled: true,
        data: [
          { value: TICKETS_TYPES.VEHICLE_SYSTEM, label: "Vehicle System" },
          { value: TICKETS_TYPES.VEHICLE_COMPONENT, label: "Vehicle Component" },
          { value: TICKETS_TYPES.EE, label: "EE Component" },
          { value: TICKETS_TYPES.SWM, label: "Software Module" },
        ],
        value: standardData.module_type,
        onChange: (selectedTypes) =>
          setStandardData((prev) => ({
            ...prev,
            module_type: selectedTypes,
          })),
      },
    },
    {
      component: "CustomFormTextField",
      props: {
        label: "Legal Standard Name",
        required: true,
        value: standardData.name,
        setValue: (val) => setStandardData((prev) => ({ ...prev, name: val })),
        placeholder: "Enter Legal Standard Name",
      },
    },
    {
      component: "CustomFormTextField",
      props: {
        label: "Description",
        required: true,
        value: standardData.description,
        setValue: (val) =>
          setStandardData((prev) => ({ ...prev, description: val })),
        placeholder: "Enter Description",
      },
    },
    {
      component: "CustomFormTextField",
      props: {
        label: "Summary",
        required: true,
        value: standardData.summary,
        setValue: (val) =>
          setStandardData((prev) => ({ ...prev, summary: val })),
        placeholder: "Enter Summary",
      },
    },
    {
      component: "CustomFormTextField",
      props: {
        label: "Revision",
        disabled: true,
        required: true,
        value: standardData.revision,
        setValue: (val) =>
          setStandardData((prev) => ({ ...prev, revision: val })),
        placeholder: "Enter Revision No",
      },
    },
    {
      component: "LegalCustomDatePicker",
      props: {
        label: "Effective From - Valid Till",

        effectiveFrom: standardData.effective_from,
        validTill: standardData.valid_till,
        setEffectiveFrom: (val) =>
          setStandardData((prev) => ({
            ...prev,
            effective_from: val,
          })),

        setValidTill: (val) =>
          setStandardData((prev) => ({
            ...prev,
            valid_till: val,
          })),

        required: true,
      },
    },
    {
      component: "ChipsTextField",
      props: {
        size: "small",
        label: "Chapters",
        disabled: true,
        variant: "outlined",
        chips: standardData?.chapters,
        setChips: (newChips) => {
          setStandardData((prev) => ({
            ...prev,
            chapters: newChips,
          }));
        },

        required: true,
      },
    },
    // {
    //   component: "CustomTextFieldWithChips",
    //   props: {
    //     label: "Add Chapters",
    //     placeholder: "Type a Chapter...",
    //     value: chapterInput,
    //     setValue: setChapterInput,
    //     onChangeChips: handleChaptersChange,
    //     required: true,
    //   },
    // },
    {
      component: "CustomMultiSelectV2",
      props: {
        placeholder: "Select Delivery Market",
        label: "Delivery Market",
        required: true,

        // >>> Correct selected values (must be array of {label, value} objects)
        selectedValues: Array.isArray(standardData.markets)
          ? standardData.markets.map((m) => ({ label: m, value: m }))
          : [],

        // >>> Correct setter (store only values inside standardData)
        setSelectedValues: (vals) =>
          setStandardData((prev) => ({
            ...prev,
            markets: vals.map((item) => item.value),
          })),

        // >>> Correct options list
        optionsList:
          adminDefaultData?.data?.product_delivery_markets?.map((item) => ({
            label: item,
            value: item,
          })) || [],
      },
    },
    {
      component: "CustomStatusOverrideSelect",
      props: {
        label: "Select Status",

        statusOptions: [
          "NEW",
          "AMMENDMENT",
          "ADDENDUM",
          "CORRECTION",
          "REVISION",
          "DEPRECATED",
        ],
        value: status,
        setValue: setStatus,
        isOverrideChecked: isOverrideChecked,
        setIsOverrideChecked: setIsOverrideChecked,
      },
    },
  ];

  const refreshLegalStandards = async (defaultFirstSelect = true) => {
    const resp = await handleGetLegalStandardList();
    const sortedGroupedDetails = groupAndSortLegalStandards(resp?.data);

    setAllLegalStandardsData(sortedGroupedDetails);
    setLegalStandardsData(sortedGroupedDetails);

    const standardList = sortedGroupedDetails?.map((el) => ({
      value: el.standard_number,
      label: el.standard_number,
    }));

    console.log("---sortedGroupedDetails---", sortedGroupedDetails);

    setStandardOptionsList(standardList);

    // Auto-select first standard
    if (standardList?.length > 0 && defaultFirstSelect) {
      setSelectedStandard(standardList[0]?.value);
    }
  };

  // ✅ Update displayed data when selection changes
  useEffect(() => {
    if (!selectedStandard) {
      // Show all if nothing selected
      setLegalStandardsData(null);
    } else {
      // Filter by selected standard
      const filtered = allLegalStandardsData.filter(
        (item) => item.standard_number === selectedStandard,
      );
      setLegalStandardsData(filtered);
    }
  }, [selectedStandard, allLegalStandardsData]);

  // ✅ Initial load
  useEffect(() => {
    refreshLegalStandards();
  }, []);

  const handleUpdateLR = (value) => {
    setOpenUpdateRevision(value);
  };
  const handleGetLegalRequirementsList = async (
    selectedLSId,
    selectedChapter,
  ) => {
    const resp = await dispatch(
      HandleApiActions({
        ...legalRequirements.get_legal_requirements_main_details,
        params: {
          page_number: 1,
          page_size: 10000,
          ...(selectedChapter && { chapter: selectedChapter }),
          ls_id: selectedLSId,
        },
        show_toast: false,
      }),
    );

    console.log("resprsrfdskfnlfnlsdnfldfdsf", resp);

    setExportSelectedLsLrDetails(resp?.data);
  };
  useEffect(() => {
    handleGetLegalRequirementsList(exportSelectedLsDetails?.ls_id);
  }, [exportSelectedLsDetails]);
  // ✅ Tabs Data
  const tabsDataLSLR = [
    {
      name: "Legal Standards",
      count: "",
      content: (
        <CustomLegalStandard
          refreshLegalStandards={refreshLegalStandards}
          handleEditClick={handleEditClick}
          legalStandardsData={legalStandardsData}
          handleOpenCloseAddLS={handleOpenCloseAddLS}
          setExportSelectedLsDetails={setExportSelectedLsDetails}
        />
      ),
    },
    {
      name: "Legal Requirements",
      count: "",
      content: (
        <CustomLegalRequirements
          handleGetLegalStandardList={handleGetLegalStandardList}
          legalStandardsData={legalStandardsData}
          refreshLegalStandards={refreshLegalStandards}
        />
      ),
    },
  ];
  // const handleUpdateLegalStandard = ({
  //   ls_id,
  //   name,
  //   description,
  //   summary,
  //   DMState,
  //   effectiveFrom,
  //   validTill,
  //   will_it_impact,
  //   status_manual,
  // }) => {
  //   console.log("will_it_impactwill_it_impactwill_it_impact", will_it_impact);
  //   if (ls_id) {
  //     let currentTime = new Date().toISOString();

  //     if (will_it_impact === true) {
  //       dispatch(
  //         HandleApiActions({
  //           ...legalRequirements.update_legal_standard,
  //           params: {
  //             ls_id,
  //             ...(name && { name }),
  //             ...(description && { description }),
  //             ...(status_manual && { status_manual }),
  //             ...(summary && { summary }),
  //             ...(DMState ? { markets: DMState } : []),
  //             ...(validTill ? { valid_till: validTill } : null),
  //             ...(effectiveFrom ? { effective_from: effectiveFrom } : null),
  //             current_time: currentTime,
  //             will_require_version: will_it_impact,
  //           },
  //           show_toast: true,
  //         }),
  //       ).then(() => {
  //         refreshLegalStandards(false);

  //         handleOpenCloseEditLS(false);
  //         setOpenForVersion(false);
  //       });
  //     } else if (will_it_impact === false) {
  //       dispatch(
  //         HandleApiActions({
  //           ...legalRequirements.update_legal_standard,
  //           params: {
  //             ls_id,
  //             ...(name && { name }),
  //             ...(description && { description }),
  //             ...(summary && { summary }),
  //             ...(DMState ? { markets: DMState } : []),
  //             ...(validTill ? { valid_till: validTill } : null),
  //             ...(effectiveFrom ? { effective_from: effectiveFrom } : null),
  //             current_time: currentTime,
  //             will_require_version: will_it_impact,
  //           },
  //           show_toast: true,
  //         }),
  //       ).then(() => {
  //         refreshLegalStandards(false);
  //         handleOpenCloseEditLS(false);
  //         setOpenForVersion(false);
  //       });
  //     }
  //   }
  // };
  const handleUpdateLegalStandard = ({
    ls_id,
    name,
    description,
    summary,
    module_type,
    DMState,
    effectiveFrom,
    validTill,
    will_it_impact,
    status_manual,
  }) => {
    console.log("will_it_impactwill_it_impactwill_it_impact", will_it_impact);

    if (!ls_id) return;

    const currentTime = new Date().toISOString();

    // 🔹 Build params once (remove duplication)
    const params = {
      ls_id,
      ...(name && { name }),
      ...(description && { description }),
      ...(summary && { summary }),
      ...(Array.isArray(module_type) && module_type.length > 0
        ? { module_type }
        : {}),
      ...(DMState ? { markets: DMState } : {}),
      ...(validTill ? { valid_till: validTill } : {}),
      ...(effectiveFrom ? { effective_from: effectiveFrom } : {}),
      current_time: currentTime,
      will_require_version: will_it_impact,
    };

    // 🔹 Only add status_manual when will_it_impact === true (same logic)
    if (will_it_impact === true && status_manual) {
      params.status_manual = status_manual;
    }

    // 🔹 Single dispatch (removed duplication)
    dispatch(
      HandleApiActions({
        ...legalRequirements.update_legal_standard,
        params,
        show_toast: true,
      }),
    ).then(() => {
      refreshLegalStandards(false);
      handleOpenCloseEditLS(false);
      setOpenForVersion(false);
    });
  };
  const handleUpdateVersionClick = async () => {
    console.log(
      "isOverrideCheckedisOverrideCheckedisOverrideChecked",
      isOverrideChecked,
    );
    if (isOverrideChecked && !status) {
      alert("Please select a manual status before updating.");
      return;
    }

    if (isOverrideChecked) {
      handleUpdateLegalStandard({
        ls_id: lsId,
        name: standardData?.name,
        description: standardData?.description,
        summary: standardData?.summary,
        module_type: standardData?.module_type,
        DMState: standardData?.markets,
        effectiveFrom: standardData?.effective_from,
        validTill: standardData?.valid_till,
        status_manual: status,
        will_it_impact: true,
      });

      await refreshLegalStandards(false);

      handleOpenCloseEditLS(false);
    } else {
      setOpenForVersion(true);
    }
  };

  const isFormValid = (d) =>
    d.name?.trim()?.length >= 3 &&
    d.description?.trim()?.length >= 3 &&
    d.summary?.trim()?.length >= 3 &&
    d.revision?.trim() &&
    Array.isArray(d.module_type) &&
    d.module_type.length > 0 &&
    d.effective_from &&
    d.valid_till &&
    Array.isArray(d.markets) &&
    d.markets.length > 0;
  const hasChanges = () => {
    return (
      JSON.stringify(standardData) !== JSON.stringify(initialStandardData) ||
      status !== initialStatus
    );
  };
  // const isEditDisabledLS = () => {
  //   const d = standardData;
  //   return !(
  //     d.name?.trim() &&
  //     d.description?.trim() &&
  //     d.revision?.trim() &&
  //     d.effective_from &&
  //     d.valid_till &&
  //     Array.isArray(d.markets) &&
  //     d.markets.length > 0
  //   );
  // };
  const isEditDisabledLS = () => {
    if (!isFormValid(standardData)) return true; // invalid form
    if (!hasChanges()) return true; // no changes made
    return false; // enable edit
  };
  const handleImportedJsonExcelFile = async ({
    fileType,
    importedFileData,
  }) => {
    console.log("importedFileDataimportedFileData", importedFileData);
    customConsole({ fileType });
    customConsole({ importedFileData });

    // Variables to store imported data
    let importedLegalStandards = {};
    let importedLegalRequirements = [];

    if (!importedFileData) {
      showToastMessage({
        type: "error",
        message: "No data found in the imported file.",
      });
      return;
    }

    // showToastMessage({
    //   type: "info",
    //   message: `Importing ${fileType === "json" ? "JSON" : "Excel"} file...`,
    // });

    if (fileType === "json") {
      try {
        if (
          typeof importedFileData !== "object" ||
          Array.isArray(importedFileData)
        ) {
          throw new TypeError("Invalid JSON file format.");
        }

        // JSON format should match the export structure
        importedLegalStandards = importedFileData
          ? {
            name: importedFileData?.name || "",
            standard_number: importedFileData?.standard_number || "",
            revision: importedFileData?.revision || "",
            description: importedFileData?.description || "",
            summary: importedFileData?.summary || "",
            module_type: importedFileData?.module_type || "",
            markets: importedFileData?.markets || [],
            chapters: importedFileData?.chapters || [],
          }
          : {};

        importedLegalRequirements = importedFileData?.legal_requirements || [];

        customConsole("Formatted JSON Legal Standards Data: ");
        customConsole({ importedLegalStandards });
        customConsole("Formatted JSON Legal Requirements Data: ");
        customConsole({ importedLegalRequirements });
        // Pass data to parent
      } catch (error) {
        showToastMessage({
          type: "error",
          message: error.message,
        });
      }
    } else if (fileType === "excel") {
      try {
        if (
          typeof importedFileData !== "object" ||
          Array.isArray(importedFileData)
        ) {
          throw new TypeError("Invalid Excel file format.");
        }

        Object.keys(importedFileData).forEach((sheetName) => {
          if (sheetName.toLowerCase() === "legal_standard") {
            if (importedFileData[sheetName]?.length > 0) {
              const ls = importedFileData[sheetName][0];
              importedLegalStandards = {
                ...ls,
                markets: ls?.markets
                  ? ls.markets.split(",").map((item) => item.trim())
                  : [],
                chapters: ls?.chapters
                  ? ls.chapters.split(",").map((item) => item.trim())
                  : [],
              };
            }
          } else if (sheetName.toLowerCase() === "legal_requirements") {
            importedLegalRequirements = importedFileData[sheetName] || [];
          }
        });

        customConsole("Formatted Excel Legal Standards Data: ");
        customConsole({ importedLegalStandards });
        customConsole("Formatted Excel Legal Requirements Data: ");
        customConsole({ importedLegalRequirements });
      } catch (error) {
        showToastMessage({
          type: "error",
          message: error.message,
        });
      }
    } else {
      showToastMessage({
        type: "error",
        message: "Unsupported file type.",
      });
    }

    // handle import legal standard and their legal requirements in the database
    let importResponse = await dispatch(
      HandleApiActions({
        ...legalRequirements.import_legal_standard_requirements,
        params: {
          ...importedLegalStandards,
          legal_requirements: importedLegalRequirements,
        },
        show_toast: true,
      }),
    );

    console.log("---importResponse---", importResponse);

    if (!importResponse?.error) {
      console.log("importResponse data", importResponse?.data);
      await refreshLegalStandards(false);
      setSelectedStandard(importResponse?.data?.standard_number);
      // console.log("---legalStandardResp---", legalStandardResp);
    }
    // .then(() => {
    //   handleGetLegalStandardList();
    // });
  };

  console.log(
    "setSelectedFilterByStatusObjBeforeClicksetSelectedFilterByStatusObjBeforeClick",
    selectedFilterByStatusObjBeforeClick.map((data) => data.value).join(","),
  );
  // ✅ Render
  return (
    <div>
      <Box sx={{ p: 2 }}>
        {/* Breadcrumb */}
        <Box
          sx={{
            padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
            flexShrink: 0,
          }}
        >
          <CommonBreadcrumbs
            items={[{ label: "Legal Requirements" }, { label: "Requirements" }]}
          />
        </Box>

        {/* Filter + Select Row */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            m: 2,
          }}
        >
          <Box>
            {/* <CustomFilterV2
              widthPerColumn={"220px"}
              sections={[
                { heading: "Sorting", content: "" },
                { heading: "Filter by connection", content: "" },
                { heading: "Filter by Page", content: "" },
                { heading: "Filter by tool", content: "" },
              ]}
            /> */}
            <CustomFilterTemplate
              showClearAll={false}
              handleDiscard={() => {
               console.log("Discard clicked")
              }}
              handleClickApply={() => {
                refreshLegalStandards();
              }}
              sections={[
                // {
                //   heading: "Sorting",
                //   content: (
                //     <SortTemplate
                //       defaultValue={selectedSortObjBeforeClick}
                //       handleOnChange={(sortValue) => {
                //         setSelectedSortObjBeforeClick(sortValue);
                //       }}
                //     />
                //   ),
                // },
                {
                  heading: "Filter By Module",
                  content: (
                    <FilterTemplate
                      defaultValue={selectedFilterByStatusObjBeforeClick}
                      handleOnChange={(sortValue) => {
                        setSelectedFilterByStatusObjBeforeClick(sortValue);
                      }}
                      data={[
                        { label: "Vehicle Systems", value: 3 },
                        { label: "Vehicle Components", value: 4 },
                        { label: "EE Components", value: 2 },
                        { label: "Software Modules", value: 1 },
                      ]}
                    />
                  ),
                },
              ]}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: "500px" }}>
              <CustomSelectV2
                showlabelNew={false}
                showLabel={false}
                label={"Legal Standard"}
                enableSearch={true}
                handleOnChange={(e) =>
                  setSelectedStandard(
                    e.target.value === "" ? null : e.target.value,
                  )
                }
                selectedValue={selectedStandard}
                optionsList={standardOptionsList || []}
                sltFldStyle={{
                  // minHeight: "45px",
                  // fontSize: "0.95rem",
                  borderRadius: 0,
                }}
              />
            </Box>
            <CustomImportButton
              handleImportedJsonExcelFile={handleImportedJsonExcelFile}
            />

            <CustomExportButton
              fileName={"Legal_Requirements"}
              exportDataJson={{
                ...exportSelectedLsDetails,
                legal_requirements: exportSelectedLsLrDetails || [],
              }}
              exportDataExcel={{
                legal_standard: exportSelectedLsDetails
                  ? [exportSelectedLsDetails]
                  : [],
                legal_requirements: exportSelectedLsLrDetails || [],
              }}
            />
          </Box>
        </Box>

        {/* Tabs */}
        {selectedStandard !== null && (
          <Box sx={{ m: 1 }}>
            <CustomTabsV2 tabs={tabsDataLSLR} />
          </Box>
        )}
      </Box>
      {openAddLS && (
        <AddDialog
          disabled={isAddDisabledLS()}
          componentMap={componentMap}
          formLayout={formLayoutAdd}
          singleColumnRows={singleColumnRows}
          open={openAddLS}
          onClose={() => handleOpenCloseAddLS(false)}
          title={"Add Legal Standard"}
          handleClickAddDialog={() => handleAddLegalStandard(reqObj)}
        />
      )}
      {openEditLS && (
        <AddDialog
          disabled={isEditDisabledLS()}
          componentMap={componentMap}
          formLayout={formLayoutEdit}
          singleColumnRows={singleColumnRows}
          open={openEditLS}
          onClose={() => handleOpenCloseEditLS(false)}
          title="Update Legal Standard"
          handleClickAddDialog={() => handleUpdateLR(true)}
          onlyForLS={true}
          versionName="Update"
          handleClickUpdateVersion={handleUpdateVersionClick}
        />
      )}
      
      {openUpdateRevision && (
        <CustomDialogBoxV2
          open={openUpdateRevision}
          onClose={() => setOpenUpdateRevision(false)}
          title={`New Revision`}
          successButtonName="Update Version"
          rejectButtonName="Cancel"
          otherButtonName="Update Revision"
          actionButtonStyle={"three"}
          typeForThree={"submit"}
          onClickSuccess={() => setOpenForVersion(true)}
          disabled={false}
          onClickReject={() => setOpenUpdateRevision(false)}
          onOtherButtonClick={() => setOpenForVersion(true)}
        >
          <Box sx={{ width: "400px" }}>
            <CustomFormTextField
              label="Revision"
              required={true}
              value={standardData.revision}
              setValue={(val) =>
                setStandardData((prev) => ({ ...prev, revision: val }))
              }
              placeholder="Enter Revision No"
            />
          </Box>
        </CustomDialogBoxV2>
      )}
      {openForVersion && (
        <Modal
          open={openForVersion}
          onClose={() => setOpenForVersion(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 320,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",

                mb: 1,
              }}
            >
              <Typography fontWeight={600} variant="subtitle1">
                Choose the Change
              </Typography>

              <IconButton onClick={() => setOpenForVersion(false)}>
                <CloseIcon fontSize="medium" />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <CustomButtonV2
                btnName="Versions"
                type="cancel"
                handleOnClick={() => {
                  handleUpdateLegalStandard({
                    ls_id: lsId,

                    effectiveFrom: standardData?.effective_from,
                    validTill: standardData?.valid_till,
                    DMState: standardData.markets,
                    ...standardData,
                    will_it_impact: true,
                  });
                }}
              />

              <CustomButtonV2
                btnName="Corrections"
                type="cancel"
                handleOnClick={() => {
                  handleUpdateLegalStandard({
                    ls_id: lsId,
                    effectiveFrom: standardData?.effective_from,
                    validTill: standardData?.valid_till,
                    DMState: standardData.markets,
                    ...standardData,
                    will_it_impact: false,
                  });
                }}
              />
            </Box>
          </Box>
        </Modal>
      )}
    </div>
  );
}

export default LegalRequirementsV2;
